/**
 * The single source of client truth for one game (M2.2).
 *
 * Consumes the server's single WebSocket state stream — `game_state` (full) and
 * `state_delta` (incremental) — and folds it into one `GameState` snapshot,
 * rendered idempotently by `version` (DESIGN §5.3, AGENT_GUIDE §5). This replaces
 * the legacy HTTP-polling + dual-sync approach with one channel, one truth.
 *
 * **Version-gap resync rule (mandatory, DESIGN §5.3):** a `state_delta` carries
 * `{fromVersion, toVersion}` and is applied ONLY if `fromVersion === localVersion`.
 * On ANY gap (missed/out-of-order delta) the store discards the delta and asks the
 * caller to resync — it emits a `resync-needed` signal so the transport sends
 * `hello` to get a fresh full `game_state`. This prevents silently applying a delta
 * onto stale state.
 *
 * Framework-agnostic on purpose (a plain observable class) so it is unit-testable
 * without React and reusable by the hook in `use-game.ts`.
 */

import { gamePhaseOf } from '@jeopardy/shared';
import type {
  GameState,
  GenerationFailureCode,
  GenerationState,
  PowerUpKind,
  PowerUpSource,
  ServerEvent,
  SneakPeekWord,
} from '@jeopardy/shared';

/** Everything the UI derives from the stream, in one immutable-ish snapshot. */
export interface GameStoreSnapshot {
  /** Latest full game state, or null before the first `game_state`. */
  state: GameState | null;
  /** The version the store has applied (mirrors `state.version`); -1 if none. */
  version: number;
  /** True once at least one `game_state` has been received. */
  hasState: boolean;
  /** The last error event from the server, if any (cleared on next full state). */
  lastError: { code: string; message: string } | null;
  /**
   * The last `generation_failed` event, if any (M12). The server has already
   * returned the game to `LOBBY` when this arrives, so — unlike `lastError` — it is
   * NOT cleared by the accompanying full `game_state`; it stays so the lobby can show
   * a "generation failed — retry" banner. Cleared when a new generation starts
   * (phase → `GENERATING`) or on an explicit dismiss.
   */
  lastGenerationFailure: { code: GenerationFailureCode; message: string } | null;
  /**
   * #232: the private **Sneak Peek** hint for the CURRENT clue, or null. Delivered by
   * a targeted `sneak_peek_revealed` event (owner-only, never broadcast — like
   * `you_have_buzzer`), so it is transient and NOT part of the versioned `game_state`.
   * Keyed by `clueId`: cleared whenever the open clue changes (a full state or delta
   * with a different — or absent — clue) so a stale peek never bleeds into the next
   * clue. Only ever holds the viewer's OWN peek (the server only sends it to them).
   */
  sneakPeek: { clueId: string; prefix: string; words: SneakPeekWord[] } | null;
  /**
   * #232: the most-recent broadcast `powerup_played`, for the TV/host "PLAYER played
   * CARD" reveal juice. `seq` increments per power-up event so a view can detect a
   * fresh one (and re-fire a transient animation). Public info only — no secret.
   * Tokens/`doubleUpBy` come from the authoritative `game_state`, not from here.
   */
  lastPowerupPlayed: {
    playerId: string;
    powerup: PowerUpKind;
    clueId: string;
    /** #232 v2 — the tapped teammate for a Tag-Team play (so their device can react). */
    target?: string;
    seq: number;
  } | null;
  /**
   * #232: the most-recent `powerup_granted`, for the player "you unlocked a card!"
   * celebration + the host activity log + the TV "new card" glow. `seq` increments per
   * power-up event. The deficit *reason* is deliberately never carried (D4 — show the
   * tool, hide the reason).
   */
  lastPowerupGranted: {
    playerId: string;
    powerup: PowerUpKind;
    source: PowerUpSource;
    seq: number;
  } | null;
  /**
   * #232 v2: the most-recent broadcast **Brain Freeze** (open, not stealth). Frozen
   * clients (everyone but `casterId`) render a "frozen" notice for `freezeMs`. `seq`
   * increments so a view can re-fire the transient. Kept keyed by `clueId` so it
   * clears when the open clue changes.
   */
  lastBrainFreeze: {
    clueId: string;
    casterId: string;
    freezeMs: number;
    seq: number;
  } | null;
  /**
   * #232 v2: the owner-only **Snake Bite** reveal — sent ONLY to a bitten target, and
   * ONLY when the bite changed the outcome. Drives the target's "you were bitten!"
   * notice. Transient; keyed by `clueId`.
   */
  lastSnakeBite: { clueId: string; seq: number } | null;
  /**
   * #232 v2: the viewer's own **Wager Boost** roll result — the die face + win
   * multiplier + the locked wager it applies to. Delivered AFTER the wager is locked;
   * drives the die → multiplier animation and the `wager × multiplier` payout preview.
   * Owner-only (never broadcast). Cleared when the wager context ends.
   */
  wagerBoost: {
    context: 'DAILY_DOUBLE' | 'FINAL_JEOPARDY';
    clueId?: string;
    face: number;
    multiplier: number;
    wager: number;
    seq: number;
  } | null;
}

function emptySnapshot(): GameStoreSnapshot {
  return {
    state: null,
    version: -1,
    hasState: false,
    lastError: null,
    lastGenerationFailure: null,
    sneakPeek: null,
    lastPowerupPlayed: null,
    lastPowerupGranted: null,
    lastBrainFreeze: null,
    lastSnakeBite: null,
    wagerBoost: null,
  };
}

export type StoreListener = (snapshot: GameStoreSnapshot) => void;

export class GameStore {
  private snapshot: GameStoreSnapshot = emptySnapshot();
  private readonly listeners = new Set<StoreListener>();
  /** Monotonic sequence stamped on each power-up juice event so views detect a fresh one. */
  private powerupSeq = 0;
  /** Called when a version gap is detected and a full resync is required. */
  private onResyncNeeded: (() => void) | null = null;

  /** Register a callback invoked when the store needs a full-state resync. */
  setResyncHandler(handler: () => void): void {
    this.onResyncNeeded = handler;
  }

  getSnapshot(): GameStoreSnapshot {
    return this.snapshot;
  }

  subscribe(listener: StoreListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Reset to the pre-connection state (e.g. on socket close before reconnect). */
  reset(): void {
    this.snapshot = emptySnapshot();
    this.emit();
  }

  /**
   * Apply one server event. Returns nothing; observers are notified via
   * {@link subscribe}. Unknown/irrelevant events are ignored (forward-compatible).
   */
  apply(event: ServerEvent): void {
    switch (event.type) {
      case 'game_state':
        return this.applyFullState(event.state, event.version);
      case 'state_delta':
        return this.applyDelta(event.fromVersion, event.toVersion, event.changes);
      case 'error':
        return this.patch({ lastError: { code: event.code, message: event.message } });
      case 'generation_status':
        // Streamed generation progress (M12). The actor broadcasts these on each
        // agent step but does NOT re-broadcast full `game_state` per tick, so the
        // client folds the stream into its authoritative `state.generation` — the
        // one truth the `GenerationStatus` view renders (the actor keeps its own copy
        // in sync identically). Without this the view is frozen at the initial
        // "Preparing…" snapshot from `start_generation`.
        return this.applyGenerationStatus(
          event.stage,
          event.message,
          event.progress,
          event.live,
        );
      case 'generation_failed':
        // Generation aborted (error/timeout/cancel). The phase has already returned
        // to LOBBY server-side; record it so the lobby shows a retry banner. It
        // survives the accompanying full `game_state` (unlike `lastError`).
        return this.patch({
          lastGenerationFailure: { code: event.code, message: event.message },
        });
      case 'sneak_peek_revealed':
        // #232: the owner-only private hint for the clue they just peeked. Transient —
        // kept keyed by clueId (cleared when the clue changes, see keepSneakPeek). Never
        // broadcast, so it can only ever be the viewer's own peek.
        return this.patch({
          sneakPeek: {
            clueId: event.clueId,
            prefix: event.prefix,
            words: event.words,
          },
        });
      case 'powerup_played':
        // #232: broadcast juice — record the most-recent play for the TV/host reveal.
        return this.patch({
          lastPowerupPlayed: {
            playerId: event.playerId,
            powerup: event.powerup,
            clueId: event.clueId,
            ...(event.target !== undefined ? { target: event.target } : {}),
            seq: (this.powerupSeq += 1),
          },
        });
      case 'powerup_granted':
        // #232: a card entered a hand — record it for the unlock celebration + host log
        // + TV glow. The reason (deficit) is never on the wire, so it can't leak here.
        return this.patch({
          lastPowerupGranted: {
            playerId: event.playerId,
            powerup: event.powerup,
            source: event.source,
            seq: (this.powerupSeq += 1),
          },
        });
      case 'brain_freeze':
        // #232 v2: broadcast Brain Freeze — frozen clients render the notice.
        return this.patch({
          lastBrainFreeze: {
            clueId: event.clueId,
            casterId: event.casterId,
            freezeMs: event.freezeMs,
            seq: (this.powerupSeq += 1),
          },
        });
      case 'snake_bite_revealed':
        // #232 v2: owner-only bite reveal — sent when a bitten target tries to buzz.
        return this.patch({
          lastSnakeBite: { clueId: event.clueId, seq: (this.powerupSeq += 1) },
        });
      case 'wager_boost_rolled':
        // #232 v2: owner-only die roll (after the wager locked) → the win multiplier.
        return this.patch({
          wagerBoost: {
            context: event.context,
            ...(event.clueId !== undefined ? { clueId: event.clueId } : {}),
            face: event.face,
            multiplier: event.multiplier,
            wager: event.wager,
            seq: (this.powerupSeq += 1),
          },
        });
      // The buzzer-smash lockout (#198, FR-4.11) is enforced entirely client-side in
      // PlayerPage, so `locked_out` needs no handling here. Everything else —
      // buzz_won / you_have_buzzer / locked_out / judged / timer_event / presence / … —
      // is followed by an authoritative `game_state`, which is what every view renders.
      // Per-client buzzer status is derived from `clueState` (activePlayerId /
      // lockedOutPlayerIds) vs the viewer's own playerId (see PlayerPage), NOT from these
      // transient per-socket events — one truth, no event/state race (DESIGN §5.3).
      default:
        return;
    }
  }

  private applyFullState(state: GameState, version: number): void {
    // Idempotent by version: a full state at an older/equal version is stale
    // (out-of-order arrival) and ignored — never regress the local view.
    if (this.snapshot.hasState && version < this.snapshot.version) return;
    this.snapshot = {
      ...this.snapshot,
      state,
      version,
      hasState: true,
      // #232: drop the private Sneak Peek hint once the open clue changes (or closes)
      // so it never bleeds into the next clue.
      sneakPeek: this.keepSneakPeek(state.jeopardy.clueState?.clueId),
      // #232 v2: the frozen/bitten notices + the wager-boost bounds are per-clue —
      // clear them when the open clue changes (or the wager context ends).
      lastBrainFreeze: keepForClue(
        this.snapshot.lastBrainFreeze,
        state.jeopardy.clueState?.clueId,
      ),
      lastSnakeBite: keepForClue(
        this.snapshot.lastSnakeBite,
        state.jeopardy.clueState?.clueId,
      ),
      wagerBoost: this.keepWagerBoost(state),
      // A fresh full authoritative state clears any stale rejection error.
      lastError: null,
      // A prior generation failure is cleared once a NEW generation starts
      // (host retried), but otherwise persists across the return-to-lobby state
      // so the retry banner survives (M12).
      lastGenerationFailure:
        gamePhaseOf(state) === 'GENERATING' ? null : this.snapshot.lastGenerationFailure,
    };
    this.emit();
  }

  /**
   * Fold a streamed `generation_status` into the authoritative `state.generation`
   * (M12). Kept in step with the actor's own `generation` block so a client that
   * connected mid-generation (which got a full `game_state`) and one following the
   * stream converge on the same view. No-op before any full state, or when the game
   * isn't `GENERATING` (a late status after settle is ignored). Does NOT bump
   * `version` — this is a display-only refinement of an existing state, not a new
   * authoritative version (so it never trips the delta version-gap rule).
   */
  private applyGenerationStatus(
    stage: GenerationState['stage'],
    message: string,
    progress: { done: number; total: number } | undefined,
    live: boolean | undefined,
  ): void {
    const state = this.snapshot.state;
    if (!state || gamePhaseOf(state) !== 'GENERATING') return;
    const generation: GenerationState = {
      stage,
      message,
      ...(progress ? { progress } : {}),
      startedAtEpochMs: state.generation?.startedAtEpochMs ?? 0,
      // Carry the store-vs-live discriminator (#346) — without this the fold would
      // strip `live`, so a live game's `live: true` would be lost on the first
      // streamed status and the six-step pipeline would never render.
      ...(live !== undefined ? { live } : {}),
    };
    this.snapshot = { ...this.snapshot, state: { ...state, generation } };
    this.emit();
  }

  private applyDelta(
    fromVersion: number,
    toVersion: number,
    changes: Partial<GameState>,
  ): void {
    // Version-gap rule (DESIGN §5.3): apply ONLY if it continues from our version.
    if (!this.snapshot.hasState || fromVersion !== this.snapshot.version) {
      // Gap (or delta before any full state): discard and request a full resync.
      this.requestResync();
      return;
    }
    const merged = {
      ...(this.snapshot.state as GameState),
      ...changes,
      version: toVersion,
    };
    this.snapshot = {
      ...this.snapshot,
      state: merged,
      version: toVersion,
      // #232: a delta may move to a new clue — keep the peek only while its clue holds.
      sneakPeek: this.keepSneakPeek(merged.jeopardy.clueState?.clueId),
      lastBrainFreeze: keepForClue(
        this.snapshot.lastBrainFreeze,
        merged.jeopardy.clueState?.clueId,
      ),
      lastSnakeBite: keepForClue(
        this.snapshot.lastSnakeBite,
        merged.jeopardy.clueState?.clueId,
      ),
      wagerBoost: this.keepWagerBoost(merged),
    };
    this.emit();
  }

  /**
   * #232: retain the current Sneak Peek hint only while its clue is still the open one;
   * any other (or absent) clue id clears it. Keeps the hint private to a single clue.
   */
  private keepSneakPeek(openClueId: string | undefined): GameStoreSnapshot['sneakPeek'] {
    const sp = this.snapshot.sneakPeek;
    return sp && sp.clueId === openClueId ? sp : null;
  }

  /**
   * #232 v2: keep the Wager Boost bounds only while the relevant wager step is live —
   * a Daily Double boost while its clue is open, a Final boost while `FINAL_WAGER`.
   */
  private keepWagerBoost(state: GameState): GameStoreSnapshot['wagerBoost'] {
    const wb = this.snapshot.wagerBoost;
    if (!wb) return null;
    if (wb.context === 'FINAL_JEOPARDY')
      return gamePhaseOf(state) === 'FINAL_WAGER' ? wb : null;
    return wb.clueId !== undefined && wb.clueId === state.jeopardy.clueState?.clueId
      ? wb
      : null;
  }

  private requestResync(): void {
    this.onResyncNeeded?.();
  }

  private patch(partial: Partial<GameStoreSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...partial };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.snapshot);
  }
}

/** #232 v2: keep a per-clue transient only while its clue is still the open one. */
function keepForClue<T extends { clueId: string }>(
  evt: T | null,
  openClueId: string | undefined,
): T | null {
  return evt && evt.clueId === openClueId ? evt : null;
}
